import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { normalizePath } from './routes';

export interface RouterValue {
  path: string;
  navigate(path: string, options?: { replace?: boolean }): void;
}

const RouterContext = createContext<RouterValue>({
  path: '/',
  navigate: () => undefined,
});

function browserPath(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return normalizePath(window.location.pathname || '/');
}

export function RouterProvider({
  children,
  initialPath,
}: {
  children: ReactNode;
  initialPath?: string;
}): JSX.Element {
  const [path, setPath] = useState<string>(() => normalizePath(initialPath ?? browserPath()));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const onPopState = (): void => setPath(browserPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: string, options?: { replace?: boolean }) => {
    const target = normalizePath(next);
    if (typeof window !== 'undefined') {
      if (options?.replace === true) {
        window.history.replaceState({}, '', target);
      } else {
        window.history.pushState({}, '', target);
      }
    }
    setPath(target);
  }, []);

  const value = useMemo<RouterValue>(() => ({ path, navigate }), [path, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  return useContext(RouterContext);
}
